// src/hooks/useReturns.ts
// Import-order (vehicle) returns — sending a vehicle back to a
// supplier/broker. Not to be confused with the old customer-facing order
// returns flow, which was removed (see answers.text).
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImportOrderReturnService } from '../admin/pages/import-orders/importOrderReturnService';
import { ReturnStatus } from '../admin/pages/import-orders/ImportOrder';

export const RETURN_KEYS = {
  all:       ['poReturns'] as const,
  lists:     () => ['poReturns', 'list'] as const,
  paginated: (page: number, search: string, status: string) =>
               ['poReturns', 'list', page, search, status] as const,
  stats:     () => ['poReturns', 'stats'] as const,
};

export interface ReturnPaginationParams {
  page: number;
  search: string;
  status: string;
  pageSize: number;
}

export const useReturnsPaginated = (params: ReturnPaginationParams) => {
  return useQuery({
    queryKey: RETURN_KEYS.paginated(params.page, params.search, params.status),
    queryFn: () =>
      ImportOrderReturnService.getReturnsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        (params.status && params.status !== 'all' ? params.status : undefined) as ReturnStatus | undefined
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

export const useReturnStats = () => {
  return useQuery({
    queryKey: RETURN_KEYS.stats(),
    queryFn: () => ImportOrderReturnService.getReturnStats(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useReturnMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: RETURN_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: (returnId: string) => ImportOrderReturnService.deleteReturn(returnId),
    onSuccess: invalidateAll,
  });

  return { remove };
};
